import { Linking, Platform } from 'react-native';

/**
 * Regular expression to match URLs in text
 * Matches http://, https://, www., and common TLDs
 * Improved regex to handle URLs with various protocols and formats
 * Pattern breakdown:
 * 1. https?://[^\s]+ - URLs with http/https protocol
 * 2. www\.[^\s]+ - URLs starting with www.
 * 3. [a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}[^\s]* - Domain names (e.g., example.com/path)
 */
const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}(?::[0-9]+)?(?:\/[^\s<>"']*)?)/gi;

/**
 * Parses text and extracts URLs, returning an array of text segments and URLs
 */
export interface TextSegment {
  type: 'text' | 'url';
  content: string;
  url?: string;
}

export function parseUrls(text: string): TextSegment[] {
  if (!text) return [];
  
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  let match;
  
  // Reset regex lastIndex
  URL_REGEX.lastIndex = 0;
  
  while ((match = URL_REGEX.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }
    
    // Add the URL
    let url = match[0];
    // Ensure URL has a protocol
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    segments.push({
      type: 'url',
      content: match[0],
      url: url
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }
  
  // If no URLs found, return the entire text as a single segment
  if (segments.length === 0) {
    segments.push({
      type: 'text',
      content: text
    });
  }
  
  return segments;
}

/**
 * Opens a URL using the Linking API
 */
export async function openUrl(url: string): Promise<void> {
  try {
    const canOpen = await Linking.canOpenURL(url);
    if (canOpen) {
      await Linking.openURL(url);
    } else {
      console.warn('Cannot open URL:', url);
      // On web, try opening in a new window as fallback
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  } catch (error) {
    console.error('Error opening URL:', error);
    // On web, try opening in a new window as fallback
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
}

